<script setup lang="ts">
import type { ButtonProps } from '#ui/types';

type BlockVariant = 'gradient' | 'primary' | 'neutral' | 'white' | 'black';

// PROPS
const { variant } = defineProps({
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    button: { type: Object as PropType<Omit<ButtonProps, 'block' | 'color' | 'variant' | 'size'>>, default: undefined },
    variant: { type: String as PropType<BlockVariant>, default: 'primary' },
});

// DATA
const bgClass = computed(() => {
    if (variant === 'gradient') return 'from-primary-500 to-primary-700 bg-linear-to-r';
    if (variant === 'neutral') return 'bg-neutral-50';
    if (variant === 'white') return 'bg-white';
    if (variant === 'black') return 'bg-black';

    return 'bg-primary-50';
});

const titleClass = computed(() => {
    if (variant === 'gradient' || variant === 'black') return 'text-inverted';
    if (variant === 'neutral') return 'text-primary-800';

    return 'text-highlighted';
});

const descriptionClass = computed(() => {
    if (variant == 'gradient') return 'text-inverted';

    return 'text-default';
});

const buttonClass = computed(() => {
    if (variant == 'gradient') return 'bg-white hover:bg-primary-100';

    return '';
});

const buttonVariant = computed<ButtonProps['variant']>(() => {
    if (variant == 'gradient') return 'outline';

    return 'solid';
});
</script>

<template>
    <div :class="bgClass">
        <BaseWidthContainer class="flex w-full flex-col items-center gap-y-8 px-6 py-12 md:px-14">
            <h2 v-if="$slots.title || !!title" class="text-3xl leading-9 font-semibold" :class="titleClass">
                <slot name="title">{{ title }}</slot>
            </h2>
            <div v-if="$slots.description || !!description" class="text-base leading-6" :class="descriptionClass">
                <slot name="description">{{ description }}</slot>
            </div>
            <slot />
            <UButton v-if="button" color="primary" :variant="buttonVariant" size="xl" :class="buttonClass" v-bind="button" />
        </BaseWidthContainer>
    </div>
</template>
